import { CURRENCY_NOT_SET, convertCurrency, currencySymbol, formatMoney, isCurrencySet } from './currency';
import { USD_TO_CNY } from '../constants/exchangeRates';
import { fromMicro, toMicro } from './decimal';
import { convertDecimalAtFixedRate } from './fixedRateConvert';

/** The price columns a catalogue supplier can carry. */
export interface SupplierPrices {
  costUSD?: number | null;
  costCNY?: number | null;
  costNGN?: number | null;
}

/**
 * A supplier's unit price in `currency`. There is no default currency: when
 * the project has none this is null, and a currency the supplier has no column
 * for is converted from its USD price (null if it has none) instead of
 * silently reusing the naira column.
 */
export function supplierPrice(supplier: SupplierPrices, currency?: string | null): number | null {
  if (!isCurrencySet(currency)) return null;
  const code = currency.trim().toUpperCase();
  if (code === 'USD') return supplier.costUSD ?? null;
  if (code === 'NGN') return supplier.costNGN ?? null;
  if (code === 'CNY') {
    if (supplier.costCNY != null) return supplier.costCNY;
    return supplier.costUSD != null ? supplier.costUSD * USD_TO_CNY : null;
  }
  return supplier.costUSD != null ? convertCurrency(supplier.costUSD, 'USD', code) : null;
}

/** USD and CNY prices keep their cents; everything else is shown whole. */
export function formatSupplierAmount(amount: number, currency?: string | null): string {
  if (!isCurrencySet(currency)) return CURRENCY_NOT_SET;
  const code = currency.trim().toUpperCase();
  if (code === 'USD' || code === 'CNY') return `${currencySymbol(code)}${amount.toFixed(2)}`;
  return formatMoney(amount, code);
}

export type SupplierCostKey = 'costNGN' | 'costUSD' | 'costCNY';

/**
 * Which supplier cost column the catalogue form edits for a project currency,
 * and the currency that column is in. The API stores NGN, USD and CNY columns;
 * any other project edits the USD column and the form labels it as USD. Null
 * while the project has no currency (the form then shows no cost field).
 */
export function supplierCostKey(currency?: string | null): { key: SupplierCostKey; currency: string } | null {
  if (!isCurrencySet(currency)) return null;
  const code = currency.trim().toUpperCase();
  if (code === 'NGN') return { key: 'costNGN', currency: 'NGN' };
  if (code === 'CNY') return { key: 'costCNY', currency: 'CNY' };
  return { key: 'costUSD', currency: 'USD' };
}

/** The suppliers with every cost column removed, for people who may not see money. */
export function withoutSupplierCosts<T extends SupplierPrices>(suppliers: readonly T[]): Omit<T, SupplierCostKey>[] {
  return suppliers.map(s => {
    const { costNGN: _n, costUSD: _u, costCNY: _c, ...rest } = s;
    return rest;
  });
}

const asDecimal = (n: number | null | undefined): string | null => {
  const micro = toMicro(n);
  return micro === null ? null : fromMicro(micro);
};

/**
 * A supplier's unit cost in `currency` as an exact decimal string (the
 * string twin of `supplierPrice`): the matching column when there is one,
 * otherwise the USD price converted at the fixed rates. Null when the cost is
 * redacted or missing, the currency is not set, or no rate exists.
 */
export function supplierCostFor(supplier: SupplierPrices, currency?: string | null): string | null {
  if (!isCurrencySet(currency)) return null;
  const code = currency.trim().toUpperCase();
  if (code === 'USD') return asDecimal(supplier.costUSD);
  if (code === 'NGN') return asDecimal(supplier.costNGN);
  if (code === 'CNY' && supplier.costCNY != null) return asDecimal(supplier.costCNY);
  return convertDecimalAtFixedRate(asDecimal(supplier.costUSD), 'USD', code);
}
